const BOT_UA_PATTERN = /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|pingdom|lighthouse|gtmetrix|prerender|headlesschrome|phantomjs/i;

const GEOCODE_CACHE_PREFIX = "kw_geo_";
const GEOCODE_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 7;

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";
const PHOTON_BASE = "https://photon.komoot.io";

export function isBotUserAgent(ua) {
  if (!ua) return false;
  return BOT_UA_PATTERN.test(ua);
}

export function isBotClient() {
  if (typeof navigator === "undefined") return false;
  return isBotUserAgent(navigator.userAgent);
}

function geocodeCacheKey(lat, lng) {
  const rLat = Number(lat).toFixed(3);
  const rLng = Number(lng).toFixed(3);
  return `${GEOCODE_CACHE_PREFIX}${rLat},${rLng}`;
}

export function readCachedGeocode(lat, lng) {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(geocodeCacheKey(lat, lng));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.t > GEOCODE_CACHE_TTL_MS) {
      sessionStorage.removeItem(geocodeCacheKey(lat, lng));
      return null;
    }
    return parsed.v;
  } catch {
    return null;
  }
}

export function writeCachedGeocode(lat, lng, value) {
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      geocodeCacheKey(lat, lng),
      JSON.stringify({ t: Date.now(), v: value })
    );
  } catch {
    // sessionStorage may be full or disabled; ignore
  }
}

function normalizeNominatimResult(item) {
  if (!item) return null;
  return {
    formatted_address: item.display_name,
    geometry: {
      location: {
        lat: () => parseFloat(item.lat),
        lng: () => parseFloat(item.lon),
      },
    },
    raw: item,
  };
}

export async function reverseGeocodeWithCache(_unused, lat, lng, { language = "en" } = {}) {
  const cached = readCachedGeocode(lat, lng);
  if (cached) {
    return { results: cached, status: "OK", cached: true };
  }

  const url = `${NOMINATIM_BASE}/reverse?format=jsonv2&lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(lng)}&accept-language=${encodeURIComponent(language)}&zoom=18&addressdetails=1`;

  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw { results: [], status: `HTTP_${res.status}` };
  }
  const data = await res.json();
  if (!data || data.error) {
    throw { results: [], status: "ZERO_RESULTS" };
  }
  const results = [normalizeNominatimResult(data)];
  writeCachedGeocode(lat, lng, results);
  return { results, status: "OK", cached: false };
}

export async function searchAddressAutocomplete(query, { language = "en", countryCode = null, limit = 5 } = {}) {
  if (!query || query.trim().length < 2) return [];

  const params = new URLSearchParams({
    q: query,
    limit: String(limit),
    lang: language,
  });
  if (countryCode && countryCode !== "int") {
    params.set("bbox", "");
  }

  const url = `${PHOTON_BASE}/api/?${params.toString()}`;
  try {
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    if (!data || !Array.isArray(data.features)) return [];

    return data.features
      .filter((f) => {
        if (!countryCode || countryCode === "int") return true;
        const cc = (f.properties && f.properties.countrycode) || "";
        return cc.toLowerCase() === countryCode.toLowerCase();
      })
      .map((f) => ({
        label: formatPhotonLabel(f),
        lat: f.geometry && f.geometry.coordinates ? f.geometry.coordinates[1] : null,
        lng: f.geometry && f.geometry.coordinates ? f.geometry.coordinates[0] : null,
        address: f.properties || {},
      }));
  } catch {
    return [];
  }
}

function formatPhotonLabel(feature) {
  const p = feature.properties || {};
  const parts = [
    [p.housenumber, p.street].filter(Boolean).join(" "),
    p.name && p.name !== p.street ? p.name : null,
    p.city || p.town || p.village,
    p.state,
    p.country,
  ].filter(Boolean);
  return Array.from(new Set(parts)).join(", ");
}
